// =============================================================================
// src/features/analytics-insights-hydrate.ts — Hidrata as edicoes VIVAS dos
// Cards Taticos (aba Resumo de IA): lista de refs + corpo da MAIS RECENTE ja
// parseado (o mount roda depois do Promise.all do bootstrap, entao o primeiro
// paint do digest nasce live sem re-render). Espelho de analytics-live-hydrate:
// memoizada, catch PROPRIO → estatico silencioso — falha do digest nao
// contamina o dashboard live nem vice-versa. Se o corpo da mais recente falhar,
// a store e LIMPA inteira: nav de 27/07 sobre conteudo estatico de 25/07 seria
// mentira pior que o fallback total.
// =============================================================================

import type { AnalyticsLiveClient } from '../data/analytics-client';
import { debugLiveIntegration } from '../data/live-debug';
import { parseAnalyticsInsightEditionMarkdown } from './analytics/analytics-insights-edition-parser';
import {
  analyticsInsightActiveEditionRef,
  analyticsInsightEditionRefs,
  applyAnalyticsInsightEditionList,
  cacheAnalyticsInsightParsedDoc,
  clearAnalyticsInsightEditions,
} from './analytics/analytics-insights-live-store';
import type { AnalyticsInsightDocKind, AnalyticsInsightEditionRef } from './analytics/analytics-insights-types';

export interface AnalyticsInsightEditionsStatus {
  readonly source: 'live' | 'static';
}

const REGEX_EDITION_DATE = /^\d{4}-\d{2}-\d{2}$/;

let insightEditionsHydration: Promise<AnalyticsInsightEditionsStatus> | null = null;

/** Hidratação memoizada; client null (env sem Hasura) → estático imediato. */
export function hydrateAnalyticsInsightEditions(
  client: AnalyticsLiveClient | null,
): Promise<AnalyticsInsightEditionsStatus> {
  if (!insightEditionsHydration) insightEditionsHydration = runInsightEditionsHydration(client);
  return insightEditionsHydration;
}

async function runInsightEditionsHydration(
  client: AnalyticsLiveClient | null,
): Promise<AnalyticsInsightEditionsStatus> {
  if (!client) {
    debugLiveIntegration('analytics-insights', 'fallback_static', { reason: 'client_null' });
    return { source: 'static' };
  }
  try {
    const editionRefs = await fetchInsightEditionRefs(client);
    if (editionRefs.length === 0) {
      debugLiveIntegration('analytics-insights', 'fallback_static', { reason: 'empty_list' });
      return { source: 'static' };
    }
    applyAnalyticsInsightEditionList(editionRefs);
    return cacheLatestInsightEditionBody(client);
  } catch (error) {
    clearAnalyticsInsightEditions();
    debugLiveIntegration('analytics-insights', 'fallback_static', {
      reason: 'fetch_edition_list_failed',
      error: error instanceof Error ? error.message : String(error),
    });
    return { source: 'static' };
  }
}

async function fetchInsightEditionRefs(client: AnalyticsLiveClient): Promise<readonly AnalyticsInsightEditionRef[]> {
  const listRows = await client.fetchInsightEditionList();
  return listRows
    .filter((listRow) => REGEX_EDITION_DATE.test(listRow.edition_date))
    .map((listRow) => ({
      editionDate: listRow.edition_date,
      docKind: normalizeInsightDocKind(listRow.doc_kind),
    }));
}

/** doc_kind chega como text do banco; qualquer valor fora do CHECK conhecido
 * cai em edicao_completa (o parser degrada p/ generico sozinho se nao for). */
function normalizeInsightDocKind(rawDocKind: string): AnalyticsInsightDocKind {
  return rawDocKind === 'nota_de_deriva' ? 'nota_de_deriva' : 'edicao_completa';
}

async function cacheLatestInsightEditionBody(
  client: AnalyticsLiveClient,
): Promise<AnalyticsInsightEditionsStatus> {
  const latestRef = analyticsInsightActiveEditionRef();
  /* v8 ignore next 2 -- guarda de estado impossivel: applyAnalyticsInsightEditionList acabou de rodar com refs nao-vazias */
  if (latestRef === null) return staticInsightFallback('latest_ref_missing');
  const bodyRow = await client.fetchInsightEditionBody(latestRef.editionDate);
  if (bodyRow === null) return staticInsightFallback('latest_body_failed');
  const parsed = parseAnalyticsInsightEditionMarkdown(
    bodyRow.markdown_body, normalizeInsightDocKind(bodyRow.doc_kind),
  );
  cacheAnalyticsInsightParsedDoc(latestRef.editionDate, parsed);
  if (parsed.parseIssues.length > 0) {
    debugLiveIntegration('analytics-insights', 'parse_issues', {
      editionDate: latestRef.editionDate,
      issues: parsed.parseIssues.map((parseIssue) => parseIssue.issueCode),
    });
  }
  debugLiveIntegration('analytics-insights', 'live_editions', {
    editions: analyticsInsightEditionRefs().length,
    latest: latestRef.editionDate,
    outcome: parsed.outcome,
  });
  return { source: 'live' };
}

/** Limpa a store e loga o motivo: nav viva sobre conteudo estatico seria
 * mentira pior que o fallback total. */
function staticInsightFallback(fallbackReason: string): AnalyticsInsightEditionsStatus {
  clearAnalyticsInsightEditions();
  debugLiveIntegration('analytics-insights', 'fallback_static', { reason: fallbackReason });
  return { source: 'static' };
}

export function __resetAnalyticsInsightsHydrationForTests(): void {
  insightEditionsHydration = null;
}
